import { inject, Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { DeliveryNotesV3Service } from 'api-v3/services';
import { SortType } from 'api-v3/models';
import { DeliveryNotesStore } from '@app/stores/deliveryNotesStore';
import { DeliveryNoteData } from '@app/shared/models/store-types';
import { SearchStore } from '@app/stores/searchStore';
import { AppStore } from '@app/stores/appStore';
import { ABORT_SIGNAL } from '@app/shared/constants/http-context-tokens';
import {
    getFullTextSearchParam,
    withColumnFilters,
} from '@app/shared/utils/paginated-search';

@Injectable({
    providedIn: 'root',
})
export class CarrierDeliveryNotesPaginatedService {
    private searchStore = inject(SearchStore);
    private appStore = inject(AppStore);
    private deliveryNotesStore = inject(DeliveryNotesStore);

    constructor(private deliveryNotesV3Service: DeliveryNotesV3Service) {}

    async loadCarrierDeliveryNotesPaginated(
        organizationSiteKey: string
    ): Promise<void> {
        // Prevent duplicate calls if already loading
        if (this.deliveryNotesStore.loading()) {
            return;
        }

        this.deliveryNotesStore.setLoading(true);
        this.deliveryNotesStore.setError(null);

        // Cancel any ongoing requests and get new abort signal
        const abortSignal = this.searchStore.cancelOngoingRequests();

        try {
            const searchText = this.searchStore.searchText();
            const pagination = this.searchStore.pagination();
            const sort = this.searchStore.sort();
            const columnFilters = this.searchStore.columnFilters();

            // Get selected locations and showClosed filter
            const selectedSiteKeys =
                this.appStore.getSelectedLocationKeys('carrier');
            const showClosed = this.appStore.showClosed();

            // Don't make API call if no organization sites are selected
            if (selectedSiteKeys.length === 0) {
                return;
            }

            const sortBy = sort.sortBy;

            // Use valid sort field or fall back to default - ensure type safety
            const validSortBy = sortBy as any;

            // Default to desc for dateCreated (newest first), use user's direction for other fields
            const sortDirection =
                validSortBy === 'dateCreated' && !sortBy
                    ? SortType.Desc // Default to newest first when no sort is specified
                    : sort.sortDirection === 'asc'
                      ? SortType.Asc
                      : SortType.Desc;

            // One query param per active column filter, AND-ed with each other
            // and with the full-text search by the backend.
            const params = withColumnFilters(
                {
                    organizationSiteKey,
                    organizationSiteKeys:
                        selectedSiteKeys.length > 0
                            ? selectedSiteKeys
                            : undefined,
                    limit: pagination.limit,
                    offset: pagination.offset,
                    sort: sortDirection,
                    sortBy: validSortBy,
                    filterNotClosed: !showClosed,
                    search: getFullTextSearchParam(searchText),
                },
                columnFilters
            );

            // Check if request was already cancelled before making API call
            if (abortSignal?.aborted) {
                return;
            }

            // Create HttpContext with abort signal for proper HTTP request cancellation
            const context = new HttpContext().set(ABORT_SIGNAL, abortSignal);

            // This will go through the HTTP interceptors and can be cancelled
            const response = await lastValueFrom(
                this.deliveryNotesV3Service.getCarrierDeliveryNotesV3(
                    params,
                    context
                )
            );

            if (response && response.data) {
                // Map the response to include orgSiteKey and ensure proper typing
                const notesWithOrgSiteKey: DeliveryNoteData[] =
                    response.data.map(note => ({
                        ...note,
                        orgSiteKey: organizationSiteKey,
                    }));

                this.deliveryNotesStore.setDeliveryNotes(notesWithOrgSiteKey);

                // Update pagination info in centralized searchStore
                this.searchStore.updatePaginationFromApiResponse(
                    response.paginationInfo
                );
            }
        } catch (error: any) {
            // Handle cancelled requests gracefully
            if (
                error?.name === 'AbortError' ||
                error?.message === 'Subscription has been cancelled' ||
                abortSignal?.aborted
            ) {
                return;
            }

            // Extract request ID from error if available
            const requestId =
                error?.headers?.get?.('x-request-id') ||
                error?.error?.requestId ||
                null;
            const errorMessage = requestId
                ? `GENERAL.ERRORS.FAILED_TO_LOAD_CARRIER_DELIVERY_NOTES|${requestId}`
                : 'GENERAL.ERRORS.FAILED_TO_LOAD_CARRIER_DELIVERY_NOTES';

            this.deliveryNotesStore.clearDeliveryNotes();
            this.deliveryNotesStore.setError(errorMessage);

            // Reset pagination to empty state on error
            this.searchStore.resetPaginationToEmpty();
        } finally {
            // Only set loading to false if request wasn't cancelled
            if (!abortSignal?.aborted) {
                this.deliveryNotesStore.setLoading(false);
            }
        }
    }
}
